import { ChatAppResponse, ChatHistoryItem, ChatSettings, ResponseError, UserLogin } from "./models";
import { storageService } from ".";

const BACKEND_URI = import.meta.env.VITE_BACKEND_URI;

export type Method = "get" | "GET" | "post" | "POST" | "put" | "PUT" | "patch" | "PATCH" | "delete" | "DELETE";
export type ResponseType = "json" | "blob" | "text";

export type ApiResponse<T> = {
    data: T;
    status: number;
    error?: ResponseError;
};

function getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/json"
    };
    // If using login, add the id token of the logged in account as the authorization
    const token = storageService.getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;

    return headers;
}

function buildUrl(url: string, params: object | null): string {
    if (!params) return url;

    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined) continue;
        search.append(key, String(value));
    }

    const query = search.toString();
    if (!query) return url;

    return `${url}${url.includes("?") ? "&" : "?"}${query}`;
}

async function parseBody<T>(response: Response, responseType?: ResponseType): Promise<T> {
    if (responseType === "blob") return (await response.blob()) as T;
    if (responseType === "text") return (await response.text()) as T;

    // 204s and empty DELETE responses have no body to parse.
    const text = await response.text();
    if (!text) return undefined as T;

    try {
        return JSON.parse(text) as T;
    } catch {
        return text as T;
    }
}

type ProblemBody = { type?: number; title?: string; detail?: string; status?: string | number };

/**
 * Reads a failed response's body as ProblemDetails, whatever responseType was requested. A few
 * responses, the rate limiter's among them, are plain text, and that text becomes the title.
 */
async function readProblem(response: Response): Promise<ResponseError> {
    const body = await parseBody<ProblemBody | string | undefined>(response);
    const problem = typeof body === "string" ? { title: body } : body;

    return {
        type: problem?.type,
        title: problem?.title,
        detail: problem?.detail,
        status: String(problem?.status ?? response.status)
    };
}

const fetchData = async <T>(
    url: string,
    method: Method = "get",
    params: object | null = null,
    body: unknown | null = null,
    headers: object | null = null,
    responseType?: ResponseType,
    signal?: AbortSignal
): Promise<ApiResponse<T>> => {
    let response: Response;

    try {
        response = await fetch(buildUrl(url, params), {
            method: method.toUpperCase(),
            headers: { ...getHeaders(), ...headers },
            body: body === null || body === undefined ? undefined : JSON.stringify(body),
            credentials: "include",
            signal
        });
    } catch (error: unknown) {
        // Network failure or abort - fetch rejects, whereas a non-2xx does not.
        return { error: { title: error instanceof Error ? error.message : "Network error" } } as unknown as ApiResponse<T>;
    }

    if (response.status === 401) {
        storageService.clearToken();
        throw Error("Not authorized");
    }

    if (!response.ok) {
        return { error: await readProblem(response) } as unknown as ApiResponse<T>;
    }

    return {
        data: await parseBody<T>(response, responseType),
        status: response.status
    };
};

/**
 * Shown whenever the backend answers 404 for a conversation — it was deleted, or it belongs to
 * another user, which the ownership filter reports the same way. Shared so the streaming and the
 * progress-polling paths say the same thing about the same condition.
 */
export const CONVERSATION_UNAVAILABLE = "This conversation is not available. It may have been deleted, or it belongs to another user.";

/**
 * What the user is told when a chat request fails, so the streaming and non-streaming paths say the
 * same thing about the same failure. The detail is kept, not dropped: for a 500 it carries the
 * trace id to quote, and it reads the same as the text of a streamed `error` event.
 */
export function describeChatFailure(error: ResponseError | undefined): string {
    if (Number(error?.status) === 404) {
        return CONVERSATION_UNAVAILABLE;
    }

    const text = [error?.title, error?.detail].filter(Boolean).join(" ");
    return text || `HTTP ${error?.status ?? "error"}`;
}

export async function chatInit(query: string, settings?: ChatSettings, signal?: AbortSignal) {
    return await fetchData<{ chatId: string }>(`${BACKEND_URI}/chat/init`, "POST", null, { query, settings }, null, undefined, signal);
}

export async function chatApi(query: string, chatId?: string, settings?: ChatSettings, signal?: AbortSignal) {
    return await fetchData<ChatAppResponse>(`${BACKEND_URI}/chat`, "POST", null, { query, chatId, settings }, null, undefined, signal);
}

/**
 * Reported when the stream closes without a `completed` or `error` event — a dropped connection,
 * a proxy timeout, a server restart. The backend keeps working on the answer after the connection
 * goes, so reopening the conversation shows it once it is finished.
 */
export const STREAM_INTERRUPTED = "The connection closed before the answer finished. Reopen the conversation to see it once it is ready.";

export async function chatApiStream(
    query: string,
    chatId: string | undefined,
    settings: ChatSettings | undefined,
    onEvent: (type: string, data: unknown) => void,
    onError: (err: Error) => void,
    signal?: AbortSignal
): Promise<void> {
    const token = storageService.getToken();
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    try {
        const response = await fetch(`${BACKEND_URI}/chat`, {
            method: "POST",
            headers,
            credentials: "include",
            body: JSON.stringify({ query, streaming: true, chatId, settings }),
            signal,
        });

        if (!response.ok) {
            throw new Error(describeChatFailure(await readProblem(response)));
        }

        if (!response.body) {
            throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let currentEvent = "";
        let ended = false;

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split("\n");
            buffer = lines.pop() ?? "";

            for (const line of lines) {
                if (line.startsWith("event: ")) {
                    currentEvent = line.slice(7).trim();
                } else if (line.startsWith("data: ")) {
                    ended ||= currentEvent === "completed" || currentEvent === "error";
                    try {
                        onEvent(currentEvent, JSON.parse(line.slice(6).trim()));
                    } catch {
                        // skip malformed data line
                    }
                    currentEvent = "";
                }
            }
        }

        if (!ended && !signal?.aborted) {
            onError(new Error(STREAM_INTERRUPTED));
        }
    } catch (err) {
        onError(err instanceof Error ? err : new Error(String(err)));
    }
}

export async function fetchProgress(chatId: string, signal?: AbortSignal) {
    return await fetchData<ChatAppResponse>(`${BACKEND_URI}/getProgress`, "GET", { chatId }, null, null, undefined, signal);
}

export async function getFileAsBlob(chatId: string, fileId: string, filename?: string, signal?: AbortSignal): Promise<string> {
    const params: Record<string, string> = { chatId, fileId };
    if (filename) params.filename = filename;
    const resp = await fetchData<Blob>(`${BACKEND_URI}/file`, "GET", params, null, null, "blob", signal);

    // A non-2xx carries an error and no body. Reject on it explicitly, rather than letting
    // createObjectURL fail on undefined and report something unrelated to what went wrong.
    if (resp.error || !resp.data) {
        throw Error(resp.error?.title || "This file is not available.");
    }

    return URL.createObjectURL(resp.data);
}

/** Rejects if the file cannot be fetched, so callers can tell the user rather than do nothing. */
export async function downloadFileAuthenticated(chatId: string, fileId: string, filename?: string): Promise<void> {
    const blobUrl = await getFileAsBlob(chatId, fileId, filename);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename ?? "file";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
}

export async function getChats(signal?: AbortSignal) {
    return await fetchData<ChatHistoryItem[]>(`${BACKEND_URI}/chats`, "GET", null, null, null, undefined, signal);
}

export async function deleteChat(chatId: string) {
    return await fetchData(`${BACKEND_URI}/chats/${encodeURIComponent(chatId)}`, "DELETE");
}

export async function deleteAllChats() {
    return await fetchData(`${BACKEND_URI}/chats`, "DELETE");
}

export async function getQuestions(noCache?: boolean) {
    const questions = await fetchData<string[]>(`${BACKEND_URI}/questions`, "GET", { noCache });

    return questions;
}

export async function login(token: string) {
    return await fetchData<UserLogin>(`${BACKEND_URI}/login`, "POST", null, {
        token
    });
}

export async function unlinkAccount(email: string, accountId: string) {
    return await fetchData<UserLogin>(`${BACKEND_URI}/account/unlink`, "POST", null, {
        userEmail: email,
        accountId
    });
}
